/**
 * @fileoverview useWebAuthnRegistration Hook - Manages WebAuthn credential registration flow
 *
 * This hook provides state management and orchestration for the WebAuthn registration
 * process, including device setup, master key generation, and credential storage.
 *
 * **Usage:**
 * ```tsx
 * const { registerDevice, isRegistering, error } = useWebAuthnRegistration();
 *
 * await registerDevice({
 *   deviceName: 'My iPhone',
 *   deviceType: 'primary',
 * });
 * ```
 *
 * @module hooks/useWebAuthnRegistration
 */

import { useState, useCallback } from 'react';
import { createLogger } from '@xanectech/lib';
import {
  registerWebAuthnCredential,
  suggestDeviceName,
  getDeviceInfo,
  type WebAuthnRegistrationResult as ServiceRegistrationResult,
} from '../services/WebAuthnService';
import {
  registerPrimaryDevice,
  registerLinkedDevice,
  listPrimaryDevices,
} from '../services/DeviceManagementService';
import {
  generateMasterKey,
  generateRecoveryKey,
  encryptMasterKeyWithRecoveryKey,
  initializeSessionWithMasterKey,
} from '@xanectech/encryption';
import { supabase } from '@xanectech/supabase-client';
import { useAuth } from '../contexts/AuthContext';

const logger = createLogger('useWebAuthnRegistration');

export interface RegistrationOptions {
  deviceName?: string;
  deviceType?: 'primary' | 'linked';
  authenticatorType?: 'platform' | 'cross-device'; // 'platform' = this device only, 'cross-device' = use iPhone/other device
  primaryDeviceId?: string; // Required if deviceType is 'linked'
}

export interface RegistrationResult {
  success: boolean;
  deviceId?: string;
  credentialId?: string;
  recoveryKey?: string[]; // 24-word recovery phrase (only for first primary device)
  error?: string;
}

export function useWebAuthnRegistration() {
  const { user } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>('');

  /**
   * Register a new WebAuthn device
   *
   * @param options - Registration options
   * @returns Registration result with device ID and recovery key (if applicable)
   */
  const registerDevice = useCallback(
    async (options: RegistrationOptions = {}): Promise<RegistrationResult> => {
      if (!user) {
        const errorMsg =
          'User must be authenticated to register WebAuthn device';
        logger.error(errorMsg);
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      setIsRegistering(true);
      setError(null);
      setProgress('Starting registration...');

      try {
        const deviceInfo = getDeviceInfo();
        const deviceName =
          options.deviceName || suggestDeviceName() || 'My Device';
        const deviceType = options.deviceType || 'primary';

        logger.info('Starting WebAuthn registration', {
          deviceType,
          deviceName,
        });

        // Step 1: Check if this is the first primary device
        const primaryDevices = await listPrimaryDevices(user.id);
        const isFirstPrimaryDevice =
          deviceType === 'primary' && primaryDevices.length === 0;

        let recoveryKey: string[] | undefined;

        // Step 2: Generate master key and recovery key (first primary device only)
        if (isFirstPrimaryDevice) {
          setProgress('Generating encryption keys...');

          const masterKey = await generateMasterKey();
          await initializeSessionWithMasterKey(user.id, masterKey.key);
          const recovery = await generateRecoveryKey();
          recoveryKey = recovery.words;

          const recoveryPhrase = recovery.words.join(' ');
          const recoveryWrappedKey = await encryptMasterKeyWithRecoveryKey(
            masterKey.keyMaterial,
            recoveryPhrase,
          );

          const { error: masterKeyStoreError } =
            await supabase.functions.invoke('store-master-key', {
              body: {
                recoveryWrappedKey,
                recoveryKeyHash: recovery.hash,
              },
            });

          if (masterKeyStoreError) {
            logger.error('Failed to store recovery wrapped key', {
              error:
                masterKeyStoreError instanceof Error
                  ? masterKeyStoreError.message
                  : String(masterKeyStoreError),
              userId: user.id,
            });
            throw new Error(
              'Failed to initialize recovery protection. Please try setup again.',
            );
          }
        }

        // Step 3: Register WebAuthn credential
        setProgress('Requesting biometric authentication...');
        const webAuthnResult = await registerWebAuthnCredential({
          userId: user.id,
          deviceName,
          // Map authenticatorType: 'platform' stays 'platform', 'cross-device' becomes 'cross-platform'
          deviceType:
            options.authenticatorType === 'cross-device'
              ? 'cross-platform'
              : 'platform',
        });

        logger.info('WebAuthn credential registered', {
          credentialId: webAuthnResult.credential.credentialId,
        });

        // Step 4: Register device in database
        setProgress('Registering device...');
        let device;

        if (deviceType === 'primary') {
          device = await registerPrimaryDevice(
            user.id,
            deviceName,
            deviceInfo,
            webAuthnResult.credential.credentialId,
          );
        } else {
          if (!options.primaryDeviceId) {
            throw new Error('Primary device ID required for linked devices');
          }
          const linkedResult = await registerLinkedDevice(
            user.id,
            deviceName,
            deviceInfo,
            options.primaryDeviceId,
          );
          device = linkedResult.device;
        }

        logger.info('Device registered successfully', {
          deviceId: device.id,
          deviceType,
        });

        setProgress('Registration complete!');
        setIsRegistering(false);

        return {
          success: true,
          deviceId: device.id,
          credentialId: webAuthnResult.credential.credentialId,
          recoveryKey,
        };
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Registration failed';
        logger.error('WebAuthn registration failed', err as Error);
        setError(errorMsg);
        setIsRegistering(false);
        setProgress('');
        return { success: false, error: errorMsg };
      }
    },
    [user],
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    registerDevice,
    isRegistering,
    error,
    progress,
    clearError,
  };
}
